import _ from 'lodash';
import { AlbumConfigConsumer } from 'contexts/AlbumConfigProvider';
import { useAuth0 } from '@auth0/auth0-react';
import { useRouter } from 'next/router';
import {
  AUTH_ERROR_GOOGLE,
  AUTH_ERROR_EMAIL,
  AUTH_ERROR_DEFAULT,
  ERROR_PAGE_404,
  ERROR_PROJECT_404,
  HOME_META_DESCRIPTION,
  HOME_META_TITLE,
  PATH_CREATOR_UPLOAD
} from 'constants';
import { ConfirmDialogConsumer } from 'contexts/ConfirmDialogProvider';
import { ChatWootConsumer } from 'contexts/ChatWootProvider';
import { SnackbarConsumer } from 'contexts/SnackbarProvider';
import AiScroller from 'components/Home/AiScroller';
import CurrentDeal from 'components/Home/CurrentDeal';
import ImperfectPhotos from 'components/Home/ImperfectPhotos';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DefaultLayout from 'layouts/DefaultLayout';
import EmailCaptureDialog from 'components/Dialog/EmailCaptureDialog';
import FeaturesTiles from 'components/Home/FeaturesTiles';
import Grid from '@mui/material/Grid';
import HeroDemo from 'components/Home/HeroDemo/HeroDemo';
import makeClasses from 'hooks/useClasses';
import PageHead from 'components/Head/PageHead';
import PeskySignup from 'components/Home/PeskySignup';
import ProductSelectDialog from 'components/Home/ProductSelectDialog';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import SectionWrapper from 'components/Home/SectionWrapper';
import Testimonials from 'components/Home/Testimonials';
import styles from 'styles/jss/components/bookSizeDialogStyle';
import useAlbumAdapter, { getProducts, getTestimonials } from 'adapters/albumAdapter';

export const getStaticProps = async () => {
  const products = await getProducts();
  const testimonials = await getTestimonials();
  return {
    props: {
      products,
      testimonials
    }
  };
};

const BetaWelcome = ({ products, testimonials }) => {
  const classes = makeClasses(styles);
  const router = useRouter();
  const [showDialog, setShowDialog] = useState(false);
  const { getProject } = useAlbumAdapter();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { project } = AlbumConfigConsumer();
  const { showConfirmDialog } = ConfirmDialogConsumer();
  const { setErrorMessage } = SnackbarConsumer();
  const { showChat, isChatInitialized } = ChatWootConsumer();
  const showCreateBook = !_.get(project, 'product.id');
  const inviteCode = _.get(router, 'query.invite');
  const currentPromo = _.get(products, '0.promo', {});

  useEffect(() => {
    if (isAuthenticated) {
      getProject();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let msg = '';
    if ('authError' in router.query) {
      switch (router.query.authError) {
        case 'GOOGLE_EXISTS':
          msg = AUTH_ERROR_GOOGLE;
          break;
        case 'EMAIL_EXISTS':
          msg = AUTH_ERROR_EMAIL;
          break;
        default:
          msg = AUTH_ERROR_DEFAULT;
      }
      router.replace('/');
      showConfirmDialog({
        title: 'Oops',
        message: msg,
        confirmButtonLabel: 'Sign in Again',
        confirmAction: () => {
          loginWithRedirect();
        }
      });
    } else if ('error' in router.query) {
      switch (router.query.error) {
        case 'GOOGLE_EXISTS':
          msg = ERROR_PROJECT_404;
          break;
        default:
          msg = ERROR_PAGE_404;
      }
      setErrorMessage(msg, 20000);
    } else if ('showProducts' in router.query) {
      setShowDialog(true);
    }
  }, [router.isReady]);

  useEffect(() => {
    if (router.asPath.indexOf('contact') > -1 && isChatInitialized) {
      showChat(true);
    }
  }, [isChatInitialized]);

  const buttonCta = showCreateBook ? 'Create Your Book' : 'Continue Your Book';

  const buttonClick = () => {
    // If the user doesn't have a project defined
    if (showCreateBook) return setShowDialog(true);
    return router.push({
      pathname: PATH_CREATOR_UPLOAD,
      query: { project: project.uuid }
    });
  };

  return (
    <>
      <ProductSelectDialog open={showDialog} handleClose={setShowDialog} />
      <PageHead
        title={HOME_META_TITLE}
        description={HOME_META_DESCRIPTION}
      />
      <Box
        sx={{
          position: 'relative',
          width: '100%'
        }}
      >
        <HeroDemo ctaClick={buttonClick} />
        <SectionWrapper
          title="What Our Fans Are Saying"
        >
          <Testimonials testimonials={testimonials} />
        </SectionWrapper>
        <SectionWrapper className={classes.imperfectWrapper}>
          <ImperfectPhotos ctaClick={buttonClick} />
        </SectionWrapper>
        <SectionWrapper>
          <CurrentDeal ctaClick={buttonClick} products={products} />
        </SectionWrapper>
        <SectionWrapper className={classes.aiScrollerWrapper}>
          <AiScroller ctaClick={buttonClick} />
        </SectionWrapper>
        <SectionWrapper altBackground>
          <FeaturesTiles />
        </SectionWrapper>
        <EmailCaptureDialog />
        {!inviteCode && (
          <SectionWrapper title="Ready to Get Started?">
            <Grid item xs={12} textAlign="center">
              <Button
                variant="contained"
                size="large"
                onClick={buttonClick}
                sx={{ mt: 2 }}
              >
                {buttonCta}
              </Button>
            </Grid>
          </SectionWrapper>
        )}
      </Box>
      <PeskySignup promo={currentPromo} />
    </>
  );
};

BetaWelcome.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  testimonials: PropTypes.arrayOf(PropTypes.shape()).isRequired
};

BetaWelcome.getLayout = (page) => (
  <DefaultLayout appBarSpacer={false}>{page}</DefaultLayout>
);

export default BetaWelcome;
